import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import { PostCacheProvider } from './context/PostCacheContext';
import { LoginModalProvider } from './context/LoginModalContext';
import { useLoginModal } from './context/LoginModalContext';
import { ActiveEditorProvider } from './context/ActiveEditorContext';
import { ListsProvider } from './context/ListsContext';
import { BookmarksProvider } from './context/BookmarksContext';
import { useSettings } from './context/SettingsContext';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import RightSidebar from './components/RightSidebar/RightSidebar';
import ShortFeed from './components/ShortFeed/ShortFeed';
import CommunityList from './components/CommunityList/CommunityList';
import PostDetail from './pages/PostDetail';
import FlexDetail from './pages/FlexDetail';
import UserProfile from './pages/UserProfile';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import Autovoting from './pages/Autovoting';
import FeedView from './pages/FeedView';
import CreateArticle from './pages/CreateArticle';
import ArticlesFeed from './pages/ArticlesFeed';
import ArticleDrafts from './pages/ArticleDrafts';
import ArticleTemplates from './pages/ArticleTemplates';
import ArticleScheduled from './pages/ArticleScheduled';
import CreateTemplate from './pages/CreateTemplate';
import HashtagFeed from './pages/HashtagFeed';
import FeedTagView from './pages/FeedTagView';
import Explore from './pages/Explore';
import ListFeed from './pages/ListFeed';
import ListsManage from './pages/ListsManage';
import BookmarkFeed from './pages/BookmarkFeed';
import BookmarksManage from './pages/BookmarksManage';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import './App.css';
import './styles/themes.css';

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <PostCacheProvider>
          <ListsProvider>
          <BookmarksProvider>
            <LoginModalProvider>
              <ActiveEditorProvider>
                <Router>
                  <InnerApp />
                </Router>
              </ActiveEditorProvider>
            </LoginModalProvider>
          </BookmarksProvider>
          </ListsProvider>
        </PostCacheProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

// Redirects to user's chosen start-feed or /feed/flex for guests
function HomePage() {
  const { user } = useAuth();
  const { startFeedPath } = useSettings();
  const location = useLocation();

  const resolveSafeStartPath = (path) => {
    const raw = String(path || '').trim();
    if (!raw) return '/feed/flex';
    if (raw === '/') return '/feed/flex';
    if (!raw.startsWith('/')) return '/feed/flex';

    // Restrict root redirect destinations to known feed entry points.
    if (raw.startsWith('/feed/') || raw.startsWith('/articles/')) {
      return raw;
    }

    return '/feed/flex';
  };

  if (!user) {
    return <Navigate to="/feed/flex" replace />;
  }

  const targetPath = resolveSafeStartPath(startFeedPath);
  if (location.pathname === targetPath) {
    return null;
  }

  return <Navigate to={targetPath} replace />;
}

function InnerApp() {
  const location = useLocation();
  const { layoutMode } = useSettings();
  const isCreateArticle = location.pathname === '/create-article' ||
    location.pathname === '/create-article/create-template';
  const isFlexDetail = location.pathname.startsWith('/post/flexdetail/') ||
    location.pathname.includes('/flexdetail');
  const isPostDetail = !isFlexDetail && (() => {
    const p = location.pathname;
    if (p.startsWith('/post/')) return true;
    if (p.startsWith('/@')) return true;
    const KNOWN_PREFIXES = new Set([
      'feed', 'explore', 'trending', 'communities', 'c', 'tag',
      'user', 'articles', 'settings', 'notifications', 'workspace',
      'create-article', 'search', 'post', 'autovoting'
    ]);
    const parts = p.split('/').filter(Boolean);
    return parts.length === 2 && !KNOWN_PREFIXES.has(parts[0]);
  })();
  const isAutovoting = location.pathname.startsWith('/autovoting');
  const isFullWidth = isCreateArticle || isPostDetail || isAutovoting;
  const { setLoginSuccessCallback } = useLoginModal();
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  useEffect(() => {
    setLoginSuccessCallback(() => setRightSidebarOpen(false));
  }, [setLoginSuccessCallback]);

  useEffect(() => {
    setIsRouteLoading(true);
    const timer = setTimeout(() => setIsRouteLoading(false), 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Close sidebars when route changes
  useEffect(() => {
    setLeftSidebarOpen(false);
    setRightSidebarOpen(false);
  }, [location.pathname]);

  // Scroll to top when route changes (except when coming back from PostDetail)
  useEffect(() => {
    const shouldPreserveScroll = sessionStorage.getItem('preserveScroll');
    if (shouldPreserveScroll) {
      sessionStorage.removeItem('preserveScroll');
      const savedPosition = sessionStorage.getItem('scrollPosition');
      if (savedPosition) {
        setTimeout(() => window.scrollTo(0, parseInt(savedPosition)), 0);
        sessionStorage.removeItem('scrollPosition');
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  useEffect(() => {
    const MODAL_SELECTORS = '.modal-overlay, .vote-slider-backdrop';

    const updateBodyScrollState = () => {
      const hasOpenModal = Boolean(document.querySelector(MODAL_SELECTORS));
      document.body.style.overflow = hasOpenModal ? 'hidden' : '';
    };

    const observer = new MutationObserver(updateBodyScrollState);
    observer.observe(document.body, { childList: true, subtree: true });
    updateBodyScrollState();

    const handleGlobalOverlayClick = (event) => {
      const overlay = event.target.closest('.modal-overlay, .vote-slider-backdrop');
      if (!overlay || event.target !== overlay) return;

      // Prefer native overlay handlers if present.
      if (typeof overlay.onclick === 'function') return;

      // Fallback: trigger a close control inside the modal.
      const closeButton = overlay.querySelector('.modal-close, .close-btn, [data-close-modal="true"]');
      if (closeButton) {
        closeButton.click();
      }
    };

    const handleGlobalEscClose = (event) => {
      if (event.key !== 'Escape') return;

      const overlays = Array.from(document.querySelectorAll(MODAL_SELECTORS));
      if (!overlays.length) return;

      const topOverlay = overlays[overlays.length - 1];
      if (!topOverlay) return;

      // Prefer close controls in the top-most modal content.
      const closeButton = topOverlay.querySelector('.modal-close, .close-btn, [data-close-modal="true"]');
      if (closeButton) {
        closeButton.click();
        return;
      }

      // If no explicit close button, simulate an overlay click.
      topOverlay.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    };

    document.addEventListener('click', handleGlobalOverlayClick);
    document.addEventListener('keydown', handleGlobalEscClose);

    return () => {
      observer.disconnect();
      document.body.style.overflow = '';
      document.removeEventListener('click', handleGlobalOverlayClick);
      document.removeEventListener('keydown', handleGlobalEscClose);
    };
  }, []);

  // Header layout mode
  if (layoutMode === 'header') {
    return (
      <div className="App header-layout">
        <Header />
        
        {/* Backdrop for mobile sidebars */}
        {(leftSidebarOpen || rightSidebarOpen) && (
          <div 
            className="sidebar-backdrop" 
            onClick={() => {
              setLeftSidebarOpen(false);
              setRightSidebarOpen(false);
            }}
          />
        )}

        <main className={`main-content${isFullWidth ? ' full-width-content' : ''}`}>
          {isRouteLoading && (
            <div className="route-loading-overlay">
              <LoadingSpinner />
            </div>
          )}
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/feed/:feedType/:sort" element={<ShortFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/feed/:feedType" element={<ShortFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/feed/:feedType/tag=:tagToSearchFor" element={<FeedTagView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/feed/:feedType/tag/:tagToSearchFor" element={<FeedTagView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/explore" element={<Explore onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/explore/:exploreType" element={<Explore onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/trending" element={<ShortFeed feedType="trending" onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/communities" element={<CommunityList onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/communities/:tab" element={<CommunityList onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/c/:community/:sort" element={<FeedView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/c/:community" element={<FeedView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/tag/:hashtag" element={<HashtagFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/@:author/:permlink/flexdetail" element={<FlexDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/@:author/:permlink" element={<PostDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/:author/:permlink/flexdetail" element={<FlexDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/:author/:permlink" element={<PostDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/post/flexdetail/:author/:permlink" element={<FlexDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/post/:author/:permlink" element={<PostDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/feed/:feedType" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/feed/:feedType/:postFilter" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/blog" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/blog/:blogType" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/lists/:listName" element={<ListFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/lists" element={<ListsManage onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/bookmarks/:folderName" element={<BookmarkFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/bookmarks/:folderId" element={<BookmarkFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/bookmarks" element={<BookmarksManage onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/following" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/followers" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/settings" element={<Settings onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/settings/:tab" element={<Settings onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/notifications" element={<Notifications onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/notifications/hidden" element={<Notifications onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/articles" element={<Navigate to="/articles/flexus/created" replace />} />
            <Route path="/articles/:app" element={<Navigate to="/articles/flexus/created" replace />} />
            <Route path="/articles/:app/:sort" element={<ArticlesFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article" element={<CreateArticle onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/drafts" element={<ArticleDrafts onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/templates" element={<ArticleTemplates onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/scheduled" element={<ArticleScheduled onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/create-template" element={<CreateTemplate onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
          </Routes>
        </main>

        <div className={`sidebar-wrapper left ${leftSidebarOpen ? 'open' : ''}`}>
          <Sidebar 
            onClose={() => setLeftSidebarOpen(false)}
            onOpenOther={() => {
              setRightSidebarOpen(false);
              setLeftSidebarOpen(true);
            }}
          />
        </div>

        {/* Mobile auth menu (header layout) */}
        <div className={`sidebar-wrapper right ${rightSidebarOpen ? 'open' : ''}`}>
          <RightSidebar 
            onClose={() => setRightSidebarOpen(false)}
            onOpenOther={() => {
              setLeftSidebarOpen(false);
              setRightSidebarOpen(true);
            }}
          />
        </div>
      </div>
    );
  }

  // Sidebar layout mode (default)
  return (
    <div className="App sidebar-layout">
      {/* Backdrop for mobile sidebars */}
      {(leftSidebarOpen || rightSidebarOpen) && (
        <div 
          className="sidebar-backdrop" 
          onClick={() => {
            setLeftSidebarOpen(false);
            setRightSidebarOpen(false);
          }}
        />
      )}
      
      {/* Layout container for sidebars and main content */}
      <div className="app-layout">
        <div className={`sidebar-wrapper left ${leftSidebarOpen ? 'open' : ''}`}>
          <Sidebar 
            onClose={() => setLeftSidebarOpen(false)}
            onOpenOther={() => {
              setRightSidebarOpen(false);
              setLeftSidebarOpen(true);
            }}
          />
        </div>
        
        <main className={`main-content${isFullWidth ? ' full-width-content' : ''}`}>
          {isRouteLoading && (
            <div className="route-loading-overlay">
              <LoadingSpinner />
            </div>
          )}
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/feed/:feedType/:sort" element={<ShortFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/feed/:feedType" element={<ShortFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/feed/:feedType/tag=:tagToSearchFor" element={<FeedTagView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/feed/:feedType/tag/:tagToSearchFor" element={<FeedTagView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/explore" element={<Explore onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/explore/:exploreType" element={<Explore onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/trending" element={<ShortFeed feedType="trending" onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/communities" element={<CommunityList onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/communities/:tab" element={<CommunityList onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/c/:community/:sort" element={<FeedView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/c/:community" element={<FeedView onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/tag/:hashtag" element={<HashtagFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/@:author/:permlink/flexdetail" element={<FlexDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/@:author/:permlink" element={<PostDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/:author/:permlink/flexdetail" element={<FlexDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/:author/:permlink" element={<PostDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/post/flexdetail/:author/:permlink" element={<FlexDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/post/:author/:permlink" element={<PostDetail onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/feed/:feedType" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/feed/:feedType/:postFilter" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/blog" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/blog/:blogType" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/lists/:listName" element={<ListFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/lists" element={<ListsManage onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/bookmarks/:folderName" element={<BookmarkFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/bookmarks/:folderId" element={<BookmarkFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/bookmarks" element={<BookmarksManage onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/following" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/user/:username/followers" element={<UserProfile onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/settings" element={<Settings onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/settings/:tab" element={<Settings onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/notifications" element={<Notifications onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/notifications/hidden" element={<Notifications onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/articles" element={<Navigate to="/articles/all/created" replace />} />
            <Route path="/articles/:app" element={<Navigate to="/articles/all/created" replace />} />
            <Route path="/articles/:app/:sort" element={<ArticlesFeed onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article" element={<CreateArticle onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/drafts" element={<ArticleDrafts onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/templates" element={<ArticleTemplates onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/scheduled" element={<ArticleScheduled onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/create-article/create-template" element={<CreateTemplate onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
            <Route path="/autovoting/*" element={<Autovoting onOpenLeftSidebar={() => setLeftSidebarOpen(true)} onOpenRightSidebar={() => setRightSidebarOpen(true)} />} />
          </Routes>
        </main>
        
        <div className={`sidebar-wrapper right ${rightSidebarOpen ? 'open' : ''}`}>
          <RightSidebar 
            onClose={() => setRightSidebarOpen(false)}
            onOpenOther={() => {
              setLeftSidebarOpen(false);
              setRightSidebarOpen(true);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
